import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useBlocker, useNavigate, useParams } from 'react-router-dom';
import CustomButton from '../reusable/CustomButton';
import PageHeader from '../reusable/PageHeader';
import Panel from '../reusable/Panel';
import Tag from '../reusable/Tag';
import Loader from '../reusable/Loader';
import KeyboardArrowLeftIcon from '../assets/keyboard-arrow-left.svg';
import KeyboardArrowRightIcon from '../assets/keyboard-arrow-right.svg';
import { NAVIGATION_LINKS_DATA } from '../../constants';
import EoaConfigurationTracker from './EoaConfigurationTracker';
import DistrictInformationFields from './DistrictInformationFields';
import AcademicsFields from './AcademicsFields';
import GradingFields from './GradingFields';
import EoaaInfoFields from './EoaaInfoFields';
import EoaaInfo2Fields from './EoaaInfo2Fields';
import SchoolInfoFields from './SchoolInfoFields';
import ReviewAndSubmit from './ReviewAndSubmit';
import ConfigurationInvalidValuesModal from './ConfigurationInvalidValuesModal';
import {
  configurationStepStatuses,
  hasInvalidConfigurationValues,
  shouldBlockInvalidConfigurationNavigation,
} from './configurationValidity';
import useConfigurationDraft from './useConfigurationDraft';
import { configurationPath, configurationStepPath, firstConfigurationStepId } from './eoaConfigurationPaths';
import {
  CONFIGURATION_STEPS,
  ConfigurationStepId,
  getConfigurationStep,
  getConfigurationStepIndex,
} from './eoaConfigurationSteps';
import './EoaConfigurationPage.css';

type ConfigurationDraft = ReturnType<typeof useConfigurationDraft>;

const STEP_FIELDS: Partial<Record<ConfigurationStepId, (draft: ConfigurationDraft) => ReactNode>> = {
  'district-information': (draft) => (
    <DistrictInformationFields value={draft.districtInformation} onChange={draft.setDistrictInformation} />
  ),
  academics: (draft) => <AcademicsFields value={draft.academics} onChange={draft.setAcademics} />,
  grading: (draft) => <GradingFields value={draft.grading} onChange={draft.setGrading} />,
  'eoaa-info-1': (draft) => (
    <EoaaInfoFields value={draft.eoaaInfo} onChange={draft.setEoaaInfo} languages={draft.surveyLanguages} />
  ),
  'eoaa-info-2': (draft) => <EoaaInfo2Fields value={draft.eoaaInfo2} onChange={draft.setEoaaInfo2} />,
  'school-info': (draft) => <SchoolInfoFields value={draft.schoolInfo} onChange={draft.setSchoolInfo} />,
};

const EoaConfigurationPage = () => {
  const { eoaId, stepId } = useParams<{ eoaId?: string; stepId?: string }>();
  const navigate = useNavigate();
  const step = getConfigurationStep(stepId);
  const currentIndex = getConfigurationStepIndex(stepId);
  const draft = useConfigurationDraft(eoaId, step?.id);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasInvalidValues = hasInvalidConfigurationValues(draft);
  const statuses = configurationStepStatuses(draft);
  const navigationBlocker = useBlocker(({ currentLocation, nextLocation }) =>
    shouldBlockInvalidConfigurationNavigation(currentLocation.pathname, nextLocation.pathname, hasInvalidValues)
  );
  const isInvalidValuesModalOpen = navigationBlocker.state === 'blocked';

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasInvalidValues) return;
      event.preventDefault();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasInvalidValues]);

  const closeInvalidValuesModal = () => {
    if (navigationBlocker.state === 'blocked') {
      navigationBlocker.reset();
    }
  };

  if (!step || currentIndex < 0) {
    return (
      <Navigate
        to={
          eoaId ? configurationPath(eoaId, firstConfigurationStepId) : configurationStepPath(firstConfigurationStepId)
        }
        replace
      />
    );
  }

  const scrollConfigurationToTop = () => {
    document.querySelector<HTMLElement>('.main-content-container')?.scrollTo({ top: 0 });
  };

  const goToStep = (id: ConfigurationStepId, scrollToTop = false) => {
    if (id === step.id) return;
    if (hasInvalidValues) {
      navigate(eoaId ? configurationPath(eoaId, id) : configurationStepPath(id));
      if (scrollToTop) requestAnimationFrame(scrollConfigurationToTop);
      return;
    }
    void (async () => {
      const savedId = await draft.persistDraft();
      navigate(savedId ? configurationPath(savedId, id) : configurationStepPath(id));
      if (scrollToTop) requestAnimationFrame(scrollConfigurationToTop);
    })();
  };

  const goToAdjacentStep = (offset: number) => {
    const nextStep = CONFIGURATION_STEPS[currentIndex + offset];
    if (nextStep) goToStep(nextStep.id, offset !== 0);
  };

  const submitEoaa = async () => {
    setIsSubmitting(true);
    try {
      const finalizedId = await draft.finalizeDraft();
      if (finalizedId) navigate(NAVIGATION_LINKS_DATA.OVERVIEW.path);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFirstStep = currentIndex === 0;
  const isLastStep = currentIndex === CONFIGURATION_STEPS.length - 1;

  return (
    <>
      <PageHeader title="Configuration" />
      <div className="main-content eoa-configuration">
        <div className="eoa-configuration-title-row">
          <h2 className="eoa-configuration-title">{eoaId ? 'Configure EOAA' : 'Create new EOAA'}</h2>
          <Tag type="progress" text="In progress" />
        </div>

        <EoaConfigurationTracker currentStepId={step.id} statuses={statuses} onStepSelect={goToStep} />

        {!draft.isHydrated ? (
          <Loader />
        ) : (
          <div className="eoa-configuration-cards">
            {step.id === 'review-and-submit' ? (
              <ReviewAndSubmit
                districtInformation={draft.districtInformation}
                academics={draft.academics}
                grading={draft.grading}
                eoaaInfo={draft.eoaaInfo}
                eoaaInfo2={draft.eoaaInfo2}
                schoolInfo={draft.schoolInfo}
                surveyLanguages={draft.surveyLanguages}
                statuses={statuses}
                onEdit={(targetStep) => goToStep(targetStep, true)}
              />
            ) : (
              (STEP_FIELDS[step.id]?.(draft) ??
              step.cardTitles.map((title) => (
                <Panel key={title} className="eoa-configuration-card">
                  <h3 className="eoa-configuration-card-title">{title}</h3>
                </Panel>
              )))
            )}
          </div>
        )}

        <div className="eoa-configuration-actions">
          <CustomButton
            className="eoa-configuration-previous"
            variant="secondary"
            size="medium"
            buttonText="Previous"
            icon={<img src={KeyboardArrowLeftIcon} alt="" width={24} height={24} />}
            iconPosition="start"
            disabled={isFirstStep}
            buttonAction={() => goToAdjacentStep(-1)}
          />
          <div className="eoa-configuration-actions-end">
            <CustomButton
              variant="secondary"
              size="medium"
              buttonText="Save and finish later"
              buttonAction={() => {
                void (async () => {
                  await draft.persistDraft();
                  navigate(NAVIGATION_LINKS_DATA.OVERVIEW.path);
                })();
              }}
            />
            {isLastStep ? (
              <CustomButton
                className="eoa-configuration-next"
                variant="primary"
                size="medium"
                buttonText="Submit EOAA"
                buttonAction={() => void submitEoaa()}
                loading={isSubmitting}
              />
            ) : (
              <CustomButton
                className="eoa-configuration-next"
                variant="primary"
                size="medium"
                buttonText="Next"
                icon={<img src={KeyboardArrowRightIcon} alt="" width={24} height={24} />}
                iconPosition="end"
                buttonAction={() => goToAdjacentStep(1)}
              />
            )}
          </div>
        </div>
      </div>
      <ConfigurationInvalidValuesModal isOpen={isInvalidValuesModalOpen} onClose={closeInvalidValuesModal} />
    </>
  );
};

export default EoaConfigurationPage;
